/**
 * Report Listing Modal
 * Allows customers to report problematic listings
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Flag, X, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  { id: "prohibited_item", label: "Prohibited item (weapons, drugs, etc.)" },
  { id: "counterfeit", label: "Counterfeit or stolen goods" },
  { id: "misleading", label: "Misleading description or images" },
  { id: "pricing_scam", label: "Pricing scam or bait-and-switch" },
  { id: "inappropriate", label: "Inappropriate content" },
  { id: "other", label: "Other violation" }
];

const ReportListingModal = ({ productId, productTitle, isOpen, onClose, token }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setDetails('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/listings/${productId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason, details })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Report submitted. Thank you for helping keep our marketplace safe.');
        onClose();
      } else {
        toast.error(data.detail || 'Failed to submit report');
      }
    } catch (err) {
      toast.error('Error submitting report');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/10 rounded-lg">
              <Flag className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Report Listing</h2>
              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{productTitle}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-700 dark:text-yellow-400">
              Reports are reviewed by our team. False reports may result in account restrictions.
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium">Why are you reporting this listing?</Label>
            <div className="mt-2 space-y-2">
              {REPORT_REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    reason === r.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={(e) => setReason(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    reason === r.id ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {reason === r.id && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Additional details (optional)</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional information that might help our review..."
              className="mt-2 min-h-[80px]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-border">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            className="flex-1"
            onClick={handleSubmit}
            disabled={submitting || !reason}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Flag className="h-4 w-4 mr-2" />
            )}
            Submit Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportListingModal;
